import { Box, Container, Grid, Paper, Skeleton, useMediaQuery, useTheme } from "@mui/material";

export function AccountPageSkeleton() {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  return (
    <Container maxWidth="xl" sx={{ py: 3, overflow: "hidden" }}>
      <Box
        sx={{
          display: "flex",
          flexDirection: isMobile ? "column" : "row",
          justifyContent: "space-between",
          alignItems: isMobile ? "flex-start" : "center",
          mb: 3,
          gap: isMobile ? 2 : 0,
        }}
      >
        <Box sx={{ width: isMobile ? "100%" : "auto" }}>
          <Skeleton
            variant="rectangular"
            width={isMobile ? "100%" : 240}
            height={48}
            sx={{ borderRadius: 1 }}
          />
        </Box>
        <Skeleton
          variant="rectangular"
          width={isMobile ? "100%" : 180}
          height={40}
          sx={{ borderRadius: 2 }}
        />
      </Box>

      <Box
        sx={{
          display: "flex",
          flexDirection: isMobile ? "column" : "row",
          justifyContent: "space-between",
          mb: 3,
          gap: 2,
        }}
      >
        <Box
          sx={{
            display: "flex",
            gap: 1,
            width: isMobile ? "100%" : "auto",
            flexWrap: isMobile ? "wrap" : "nowrap",
          }}
        >
          <Skeleton
            variant="rectangular"
            width={isMobile ? "48%" : 140}
            height={36}
            sx={{ borderRadius: 2, flexGrow: isMobile ? 1 : 0 }}
          />
          <Skeleton
            variant="rectangular"
            width={isMobile ? "48%" : 100}
            height={36}
            sx={{ borderRadius: 2, flexGrow: isMobile ? 1 : 0 }}
          />
        </Box>
        <Box
          sx={{
            display: "flex",
            gap: 1,
            width: isMobile ? "100%" : "auto",
            flexWrap: "wrap",
          }}
        >
          <Skeleton
            variant="rectangular"
            width={isMobile ? "48%" : 100}
            height={36}
            sx={{ borderRadius: 2, flexGrow: isMobile ? 1 : 0 }}
          />
          <Skeleton
            variant="rectangular"
            width={isMobile ? "48%" : 80}
            height={36}
            sx={{ borderRadius: 2, flexGrow: isMobile ? 1 : 0 }}
          />
          <Skeleton
            variant="rectangular"
            width={isMobile ? "100%" : 180}
            height={36}
            sx={{ borderRadius: 2 }}
          />
        </Box>
      </Box>

      <Grid container spacing={2}>
        <Grid item xs={12} md={6}>
          <Paper elevation={0} variant="outlined" sx={{ p: 3, borderRadius: 2, height: "100%" }}>
            <Box
              sx={{
                display: "flex",
                flexDirection: isMobile ? "column" : "row",
                justifyContent: "space-between",
                mb: 2,
                gap: isMobile ? 2 : 0,
              }}
            >
              <Box>
                <Box sx={{ display: "flex", alignItems: "center", mb: 0.5 }}>
                  <Skeleton variant="text" width={120} height={28} />
                </Box>
                <Skeleton variant="text" width={200} height={20} />
                <Box sx={{ display: "flex", alignItems: "center", mt: 0.5 }}>
                  <Skeleton variant="text" width={80} height={20} />
                </Box>
              </Box>
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <Skeleton variant="rectangular" width={40} height={20} sx={{ borderRadius: 10 }} />
                <Skeleton variant="circular" width={24} height={24} />
              </Box>
            </Box>

            <Box sx={{ display: "flex", alignItems: "center", gap: 2, my: 3 }}>
              <Skeleton variant="circular" width={40} height={40} />
              <Box>
                <Skeleton variant="text" width={20} height={32} />
                <Skeleton variant="text" width={160} height={20} />
              </Box>
            </Box>

            <Grid container spacing={2}>
              {[1, 2, 3, 4].map((item) => (
                <Grid item xs={12} sm={6} key={item}>
                  <Paper
                    elevation={0}
                    sx={{ p: 2, bgcolor: "rgba(0, 0, 0, 0.02)", borderRadius: 2 }}
                  >
                    <Box>
                      <Skeleton variant="circular" width={24} height={24} sx={{ mb: 1 }} />
                      <Skeleton
                        variant="text"
                        width="100%"
                        maxWidth={180}
                        height={20}
                        sx={{ mb: 1 }}
                      />
                      <Skeleton variant="text" width={40} height={32} />
                    </Box>
                  </Paper>
                </Grid>
              ))}
            </Grid>
          </Paper>
        </Grid>

        <Grid item xs={12} md={6}>
          <Paper
            elevation={0}
            variant="outlined"
            sx={{
              p: 3,
              borderRadius: 2,
              height: "100%",
              display: "flex",
              flexDirection: "column",
              justifyContent: "center",
              alignItems: "center",
              borderStyle: "dashed",
            }}
          >
            <Skeleton variant="circular" width={60} height={60} sx={{ mb: 2 }} />
            <Skeleton variant="text" width={isMobile ? "80%" : 180} height={28} />
          </Paper>
        </Grid>
      </Grid>

      <Box
        sx={{
          display: "flex",
          flexDirection: isMobile ? "column" : "row",
          justifyContent: "space-between",
          mt: 3,
          alignItems: isMobile ? "flex-start" : "center",
          gap: isMobile ? 2 : 0,
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <Skeleton variant="rectangular" width={60} height={32} sx={{ borderRadius: 1 }} />
          <Skeleton variant="text" width={80} height={24} />
        </Box>
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 1,
            width: isMobile ? "100%" : "auto",
            justifyContent: isMobile ? "space-between" : "flex-end",
          }}
        >
          <Skeleton variant="text" width={isMobile ? "30%" : 80} height={24} />
          <Box sx={{ display: "flex" }}>
            {isMobile ? (
              <>
                <Skeleton variant="circular" width={32} height={32} sx={{ mx: 0.5 }} />
                <Skeleton
                  variant="rectangular"
                  width={40}
                  height={32}
                  sx={{ mx: 1, borderRadius: 1 }}
                />
                <Skeleton variant="circular" width={32} height={32} sx={{ mx: 0.5 }} />
              </>
            ) : (
              <>
                <Skeleton variant="circular" width={32} height={32} sx={{ mx: 0.5 }} />
                <Skeleton variant="circular" width={32} height={32} sx={{ mx: 0.5 }} />
                <Skeleton
                  variant="rectangular"
                  width={40}
                  height={32}
                  sx={{ mx: 1, borderRadius: 1 }}
                />
                <Skeleton variant="circular" width={32} height={32} sx={{ mx: 0.5 }} />
                <Skeleton variant="circular" width={32} height={32} sx={{ mx: 0.5 }} />
              </>
            )}
          </Box>
        </Box>
      </Box>
    </Container>
  );
}
